import PropTypes from 'prop-types'
import { useState } from 'react'
import { createPortal } from 'react-dom'

const InlineImage = ({ src, alt, caption, className }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  const handleLoad = () => {
    setIsLoading(false)
  }

  const handleError = () => {
    setIsLoading(false)
    setHasError(true)
  }

  const handleClick = (e) => {
    e.stopPropagation()
    if (!hasError && !isLoading) {
      setIsExpanded(!isExpanded)
    }
  }

  if (hasError) {
    return (
      <div className="block my-4 mx-auto max-w-md">
        <div className="bg-dark-tertiary/50 backdrop-blur border border-dark-border rounded-xl p-6 text-center">
          <div className="text-gray-400">
            <div className="text-4xl mb-3 opacity-50">🖼️</div>
            <div className="text-sm font-medium mb-1">Image couldn&apos;t load</div>
            <div className="text-xs text-gray-500">{alt || 'No description available'}</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <figure
        className={`inline-block my-6 max-w-full relative ${className || ''}`}
        style={{ isolation: 'isolate' }}
      >
        <div className="relative group">
          <div
            className={`relative overflow-hidden rounded-xl transition-all duration-300 ${
              !isLoading && !hasError ? 'cursor-pointer hover:shadow-xl' : ''
            }`}
            onClick={handleClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleClick(e)
              }
            }}
          >
            {isLoading && (
              <div className="absolute inset-0 bg-dark-tertiary/80 backdrop-blur rounded-xl animate-pulse flex flex-col items-center justify-center z-10">
                <div className="w-8 h-8 border-2 border-gray-600 border-t-gray-400 rounded-full animate-spin mb-3" />
                <span className="text-gray-400 text-sm font-medium">Loading image...</span>
              </div>
            )}
            <img
              src={src}
              alt={alt || ''}
              onLoad={handleLoad}
              onError={handleError}
              className={`block max-w-full h-auto rounded-xl border border-dark-border/50 ${
                isLoading ? 'opacity-0' : 'opacity-100'
              } transition-all duration-500 ${
                !isLoading && !hasError ? 'group-hover:scale-[1.02] hover:border-dark-border' : ''
              }`}
              style={{ maxHeight: isExpanded ? '80vh' : '400px', objectFit: 'contain' }}
            />
            {!isLoading && !hasError && (
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <div className="bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-lg flex items-center gap-1">
                  {isExpanded ? (
                    <>
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                      <span>Collapse</span>
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 8V4m0 0h4M4 4l5 5m11-5h-4m4 0v4m0-4l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5h-4m4 0v-4m0 4l-5-5"
                        />
                      </svg>
                      <span>Expand</span>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
          {caption && (
            <figcaption className="mt-3 text-xs text-gray-400 text-center italic">
              {caption}
            </figcaption>
          )}
        </div>
      </figure>

      {/* Fullscreen overlay when expanded - rendered in portal */}
      {isExpanded &&
        !isLoading &&
        !hasError &&
        createPortal(
          <div
            className="fixed inset-0 bg-black/90 backdrop-blur-md flex flex-col items-center justify-center p-4 animate-fade-in"
            style={{ zIndex: 99999 }}
            onClick={() => setIsExpanded(false)}
          >
            <div className="flex flex-col items-center max-w-full max-h-full">
              <img
                src={src}
                alt={alt || ''}
                className="max-w-full max-h-[85vh] object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
              {caption && (
                <div className="mt-4 max-w-2xl" onClick={(e) => e.stopPropagation()}>
                  <p className="text-gray-300 text-sm text-center italic px-4">{caption}</p>
                </div>
              )}
            </div>
            <button
              onClick={() => setIsExpanded(false)}
              className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
              aria-label="Close fullscreen"
            >
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>,
          document.body
        )}
    </>
  )
}

InlineImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  caption: PropTypes.string,
  className: PropTypes.string,
}

export default InlineImage
